import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import setSimulationTickPromptTemplates from 'redux/actions/Common/setSimulationTickPromptTemplates';
import { TICK_PROMPT_PLACEHOLDER_HELP } from './simulationUtils';

/**
 * Simulation-level editor for the seller and buyer tick prompts (PATCH
 * …/tick-prompt-template). Unlike personas, the current templates come back
 * from the API, so this always reflects the stored value. Blank means the
 * built-in default. Takes effect on the very next tick, including while
 * running.
 */
const TickPromptEditor = ({
  simulationId,
  sellerTemplate,
  buyerTemplate,
  disabled,
  onSaved,
  onError,
}) => {
  const dispatch = useDispatch();
  const savedSeller = sellerTemplate || '';
  const savedBuyer = buyerTemplate || '';

  const [seller, setSeller] = useState(savedSeller);
  const [buyer, setBuyer] = useState(savedBuyer);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSeller(savedSeller);
    setBuyer(savedBuyer);
  }, [savedSeller, savedBuyer]);

  const dirty = seller !== savedSeller || buyer !== savedBuyer;

  const handleSave = () => {
    if (!dirty || saving) return;
    setSaving(true);
    dispatch(
      setSimulationTickPromptTemplates(
        simulationId,
        { sellerTemplate: seller.trim(), buyerTemplate: buyer.trim() },
        (data) => {
          setSaving(false);
          onSaved({
            sellerTemplate: data?.sellerTemplate ?? null,
            buyerTemplate: data?.buyerTemplate ?? null,
          });
        },
        (message) => {
          setSaving(false);
          onError(message);
        },
      ),
    );
  };

  const isCustomized = Boolean(savedSeller || savedBuyer);

  return (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant={'subtitle2'} fontWeight={'bold'}>
          Tick prompts {isCustomized ? '(customized)' : '(default)'}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {disabled ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            This simulation has finished — tick prompts can no longer be
            changed.
          </Alert>
        ) : null}
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={12}
            size="small"
            label="Seller tick prompt"
            value={seller}
            onChange={(e) => setSeller(e.target.value)}
            disabled={disabled || saving}
            placeholder="Leave blank to use the built-in default"
            helperText={`Placeholders: ${TICK_PROMPT_PLACEHOLDER_HELP}`}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={12}
            size="small"
            label="Buyer tick prompt"
            value={buyer}
            onChange={(e) => setBuyer(e.target.value)}
            disabled={disabled || saving}
            placeholder="Leave blank to use the built-in default"
            helperText={`Placeholders: ${TICK_PROMPT_PLACEHOLDER_HELP}`}
          />
        </Box>
        <Box display={'flex'} justifyContent={'flex-end'} gap={1} mt={1.5}>
          <Button
            size="small"
            onClick={() => {
              setSeller('');
              setBuyer('');
            }}
            disabled={disabled || saving || (!seller && !buyer)}
          >
            Clear to defaults
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={disabled || saving || !dirty}
          >
            {saving ? (
              <>
                Saving...
                <CircularProgress size={14} sx={{ ml: 1 }} />
              </>
            ) : (
              'Save tick prompts'
            )}
          </Button>
        </Box>
        <Typography
          variant={'caption'}
          color={'text.secondary'}
          display={'block'}
          mt={0.5}
        >
          Takes effect on the next tick — use “Force tick” to see it right
          away. The fixed behavioural instructions are always appended and
          can’t be edited here.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export default TickPromptEditor;
